import { useMemo } from 'react';
import {
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usesUrlaStyleSections } from '@/config/loanProductRegistry';
import { usePatchLoanDeclarationsMutation } from '@/apis/extended/loanApplicationApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import type { LoanApplication } from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

type DeclState = {
  selfEmployed: boolean;
  firstTimeHomeBuyer: boolean;
  firstTimeHomeInvestor: boolean;
  ruralProperty: boolean;
  estimatedCreditScore: string;
  bankruptcy: string;
  foreclosure: string;
  deedInLieu: string;
  shortSale: string;
  mortgageLates: string;
  monthsReserve: string;
  judgmentLien: string;
  partyToLawsuit: string;
  borrowedDownPayment: string;
  giftDownPayment: string;
};

function isInvestorLoanApplication(app: LoanApplication): boolean {
  return (
    app.flowProfile === 'investor_dscr' ||
    app.productType === 'dscr' ||
    app.productType === 'bridge'
  );
}

const opts = ['No', 'Yes', 'Unsure'];

function fromApp(decl: Record<string, unknown> | undefined): DeclState {
  const d = decl || {};
  return {
    selfEmployed: Boolean(d.selfEmployed),
    firstTimeHomeBuyer: Boolean(d.firstTimeHomeBuyer),
    firstTimeHomeInvestor: Boolean(d.firstTimeHomeInvestor),
    ruralProperty: Boolean(d.ruralProperty),
    estimatedCreditScore:
      d.estimatedCreditScore != null ? String(d.estimatedCreditScore) : '',
    bankruptcy: String(d.bankruptcy ?? ''),
    foreclosure: String(d.foreclosure ?? ''),
    deedInLieu: String(d.deedInLieu ?? ''),
    shortSale: String(d.shortSale ?? ''),
    mortgageLates: String(d.mortgageLates ?? ''),
    monthsReserve: d.monthsReserve != null ? String(d.monthsReserve) : '',
    judgmentLien: String(d.judgmentLien ?? ''),
    partyToLawsuit: String(d.partyToLawsuit ?? ''),
    borrowedDownPayment: String(d.borrowedDownPayment ?? ''),
    giftDownPayment: String(d.giftDownPayment ?? ''),
  };
}

export function DeclarationsSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanDeclarationsMutation();
  const { data: user } = useGetCurrentUserQuery();
  const isRefinance = application.flowProfile === 'refinance';
  // Residential files ask for the credit score once, in "Your loan".
  const askCreditScore = !usesUrlaStyleSections(
    application.productType,
    application.flowProfile
  );
  const profileCreditScore = user?.loanInfo?.creditScore;

  const initial = useMemo(() => {
    const base = fromApp(application.declarations);
    if (!base.estimatedCreditScore && profileCreditScore) {
      base.estimatedCreditScore = String(profileCreditScore);
    }
    // Already answered in "Your loan" — where the down payment comes from.
    const source = application.agencyFields?.downPaymentSource;
    if (
      !base.giftDownPayment &&
      (source === 'Gift from family' || source === 'Down payment assistance')
    ) {
      base.giftDownPayment = 'Yes';
    }
    return base;
  }, [
    application._id,
    application.declarations,
    application.updatedAt,
    application.agencyFields?.downPaymentSource,
    profileCreditScore,
  ]);

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'declarations',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initial,
    readOnly,
  });

  const toPayload = (s: DeclState): Record<string, unknown> => ({
    selfEmployed: s.selfEmployed,
    firstTimeHomeBuyer: isRefinance ? false : s.firstTimeHomeBuyer,
    firstTimeHomeInvestor: isInvestorLoanApplication(application)
      ? s.firstTimeHomeInvestor
      : false,
    ruralProperty: s.ruralProperty,
    estimatedCreditScore: s.estimatedCreditScore
      ? Number(s.estimatedCreditScore)
      : undefined,
    bankruptcy: s.bankruptcy || undefined,
    foreclosure: s.foreclosure || undefined,
    deedInLieu: s.deedInLieu || undefined,
    shortSale: s.shortSale || undefined,
    mortgageLates: s.mortgageLates || undefined,
    monthsReserve: s.monthsReserve ? Number(s.monthsReserve) : undefined,
    judgmentLien: s.judgmentLien || undefined,
    partyToLawsuit: s.partyToLawsuit || undefined,
    borrowedDownPayment: isRefinance
      ? undefined
      : s.borrowedDownPayment || undefined,
    giftDownPayment: isRefinance ? undefined : s.giftDownPayment || undefined,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({
        id: application._id,
        declarations: toPayload(local),
      }).unwrap();
      markSaved(local);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;
  const showFirstTimeInvestor = isInvestorLoanApplication(application);

  const selField = (
    key: keyof Pick<
      DeclState,
      | 'bankruptcy'
      | 'foreclosure'
      | 'deedInLieu'
      | 'shortSale'
      | 'mortgageLates'
      | 'judgmentLien'
      | 'partyToLawsuit'
      | 'borrowedDownPayment'
      | 'giftDownPayment'
    >,
    label: string
  ) => (
    <TextField
      fullWidth
      select
      label={label}
      value={local[key] as string}
      disabled={disabled}
      onChange={(e) => setLocal((s) => ({ ...s, [key]: e.target.value }))}
    >
      <MenuItem value="">—</MenuItem>
      {opts.map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Questions every lender is required to ask. Most people answer “No” to
        all of them.
      </Typography>
      <Grid container spacing={2}>
        {askCreditScore && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Estimated credit score"
              type="number"
              value={local.estimatedCreditScore}
              disabled={disabled}
              onChange={(e) =>
                setLocal((s) => ({
                  ...s,
                  estimatedCreditScore: e.target.value,
                }))
              }
              helperText="From your pre-qualification"
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Months of reserves"
            type="number"
            value={local.monthsReserve}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({ ...s, monthsReserve: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={local.selfEmployed}
                disabled={disabled}
                onChange={(_, v) =>
                  setLocal((s) => ({ ...s, selfEmployed: v }))
                }
              />
            }
            label="Self-employed"
          />
        </Grid>
        {!isRefinance && (
          <Grid item xs={12} md={showFirstTimeInvestor ? 4 : 6}>
            <FormControlLabel
              control={
                <Switch
                  checked={local.firstTimeHomeBuyer}
                  disabled={disabled}
                  onChange={(_, v) =>
                    setLocal((s) => ({ ...s, firstTimeHomeBuyer: v }))
                  }
                />
              }
              label="First-time homebuyer"
            />
          </Grid>
        )}
        {showFirstTimeInvestor && (
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={local.firstTimeHomeInvestor}
                  disabled={disabled}
                  onChange={(_, v) =>
                    setLocal((s) => ({ ...s, firstTimeHomeInvestor: v }))
                  }
                />
              }
              label="First-time investor"
            />
          </Grid>
        )}
        <Grid item xs={12} md={showFirstTimeInvestor ? 4 : 6}>
          <FormControlLabel
            control={
              <Switch
                checked={local.ruralProperty}
                disabled={disabled}
                onChange={(_, v) =>
                  setLocal((s) => ({ ...s, ruralProperty: v }))
                }
              />
            }
            label="Rural property"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {selField('bankruptcy', 'Bankruptcy (ever)')}
        </Grid>
        <Grid item xs={12} md={6}>
          {selField('foreclosure', 'Foreclosure')}
        </Grid>
        <Grid item xs={12} md={6}>
          {selField('deedInLieu', 'Deed in lieu')}
        </Grid>
        <Grid item xs={12} md={6}>
          {selField('shortSale', 'Short sale')}
        </Grid>
        <Grid item xs={12} md={6}>
          {selField('mortgageLates', 'Mortgage lates')}
        </Grid>
        <Grid item xs={12} md={6}>
          {selField('judgmentLien', 'Outstanding judgments / liens')}
        </Grid>
        <Grid item xs={12} md={6}>
          {selField('partyToLawsuit', 'Party to lawsuit / financial dispute')}
        </Grid>
        {/* A refinance has no down payment to borrow or be gifted. */}
        {!isRefinance && (
          <>
            <Grid item xs={12} md={6}>
              {selField(
                'borrowedDownPayment',
                'Is any of the down payment borrowed?'
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {selField('giftDownPayment', 'Is any of it a gift or grant?')}
            </Grid>
          </>
        )}
      </Grid>
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save declarations"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
